import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OfflineBanner from './OfflineBanner';
import SyncCenter from './SyncCenter';
import PinInput from './PinInput';
import Sheet from '../common/Sheet';
import { useAuth } from '../../context/authcontext';
import { OPEN_SYNC_CENTER_EVENT } from '../../hooks/useSyncStatus';
import { useOfflineStatus, useOnlineStatus } from '../../hooks/useOfflineDevice';
import { OfflineSession } from '../../services/offline/offlineSession';
import { OfflineMode } from '../../services/offline/offlineMode';
import { DeviceContext } from '../../services/offline/deviceContext';
import { uploadOrphanEscrow } from '../../services/offline/offlineServices';
import { flushLegacyDraftsOnline } from '../../services/offline/draftMigration';
import { createChartEntry } from '../../services/patientmr/charting.service';
import type { EntryType } from '../../dtos/patientmr/charting';

export const SYNC_CENTER_PATH = '/sync';

/** Route /sync (Menu → Sync, banner, chips). */
export const SyncCenterRoute: React.FC = () => {
  const online = useOnlineStatus();
  const navigate = useNavigate();
  return <SyncCenter online={online} onClose={() => navigate('/dashboard')} />;
};

/**
 * R-311 (plan C12): after a password sign-in on an enrolled phone that still holds unsynced work,
 * ask for the PIN to unlock the store so it can sync. Skipping keeps the work encrypted.
 */
export const SyncUnlockPrompt: React.FC = () => {
  const { user } = useAuth();
  const status = useOfflineStatus();
  const [pending, setPending] = useState<{ identityId: string; count: number } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.tenantId || !user.id || status === 'unlocked' || !DeviceContext.isMobile()) { setPending(null); return; }
    OfflineSession.lockedPendingCount(user.tenantId, user.id).then((r) => setPending(r && r.count > 0 ? r : null)).catch(() => setPending(null));
  }, [user?.tenantId, user?.id, status]);

  if (!pending || dismissed || status === 'unlocked') return null;

  const unlock = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await OfflineSession.unlock(pending.identityId, pin);
      if (result.ok) {
        // Online session is already active: restart the offline window; the engine starts on unlock
        await OfflineSession.markOnlineAuth(user || undefined).catch(() => undefined);
        setPending(null);
      } else if ('attemptsLeft' in result) setError(`Wrong PIN. ${result.attemptsLeft} ${result.attemptsLeft === 1 ? 'try' : 'tries'} left before offline data is erased.`);
      else setError('Offline data on this device was erased. Sealed copies of your work are still sent to the office.');
    } finally {
      setPin('');
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={() => setDismissed(true)}
      title={`Sync ${pending.count} item${pending.count === 1 ? '' : 's'}`}
      subtitle="Work saved on this device while offline"
      footer={(
        <div className="flex gap-2">
          <button type="button" onClick={() => setDismissed(true)} disabled={busy} className="min-h-[48px] flex-1 rounded-lg border border-gray-300 text-base font-medium text-gray-700">Not now</button>
          <button type="button" onClick={unlock} disabled={busy || pin.length !== 6} className="min-h-[48px] flex-[2] rounded-lg bg-slate-800 text-base font-semibold text-white disabled:opacity-50">{busy ? 'Unlocking…' : 'Unlock and sync'}</button>
        </div>
      )}
    >
      <p className="mb-3 text-sm text-gray-700">Enter your offline PIN to send it now. If you skip, it stays encrypted on this device.</p>
      <PinInput id="sync-unlock-pin" label="Offline PIN" value={pin} onChange={setPin} disabled={busy} autoFocus />
      {error && <p role="alert" className="mt-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    </Sheet>
  );
};

/**
 * Field-mode furniture inside the router (Phase 16C): the offline banner on every screen, Sync
 * Center navigation, the R-311 PIN prompt, and one-time sign-in tasks (orphaned escrow upload,
 * Phase 9B drafts sent online on devices without offline mode).
 */
const FieldModeShell: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const open = () => navigate(SYNC_CENTER_PATH);
    window.addEventListener(OPEN_SYNC_CENTER_EVENT, open);
    return () => window.removeEventListener(OPEN_SYNC_CENTER_EVENT, open);
  }, [navigate]);

  useEffect(() => {
    if (!user?.id || !user.tenantId) return;
    uploadOrphanEscrow(user.tenantId, user.id).catch(() => undefined);
    if (!OfflineMode.enabled()) {
      flushLegacyDraftsOnline(user.id, (patientId, visitId, payload) => createChartEntry(patientId, visitId, { entryType: payload.entryType as EntryType, data: payload.data as never })).catch(() => undefined);
    }
  }, [user?.id, user?.tenantId]);

  return (
    <>
      <OfflineBanner onOpenSyncCenter={() => navigate(SYNC_CENTER_PATH)} />
      <SyncUnlockPrompt />
    </>
  );
};

export default FieldModeShell;
